import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpStatus } from "./api/client";
import { routeTree } from "./routeTree.gen";
import "./styles/globals.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error: unknown) => {
        // Never retry 4xx — those are our own bugs or auth failures,
        // not transient. Retry everything else up to twice.
        const status = httpStatus(error);
        if (status !== null && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

// Mocked backend for walkthroughs — local dev AND deployed non-prod
// envs (my-dev / my-uat), always behind the explicit `?mock` URL
// flag. `?mock=off` clears it for the tab. VITE_ENV is statically
// replaced at build time, so this whole branch (and the mock module)
// is dead-code-eliminated from production bundles.
if (import.meta.env.DEV || import.meta.env.VITE_ENV !== "prod") {
  const params = new URLSearchParams(window.location.search);
  if (params.get("mock") === "off") {
    sessionStorage.removeItem("memily-dev-mock");
  } else if (params.has("mock")) {
    sessionStorage.setItem("memily-dev-mock", "1");
  }
  if (sessionStorage.getItem("memily-dev-mock") === "1") {
    const { installDevMock } = await import("./lib/dev-mock");
    installDevMock();
  }
}

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
